import React, { useEffect, useState } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import {
    AppBar,
    Button,
    Toolbar,
    Menu,
    MenuItem,
    Badge,
    IconButton,
    Tooltip,
    ListSubheader,
    Divider
} from '@material-ui/core';
import GuestHeaderContainer from '../containers/GuestHeaderContainer';
import { MODAL_TYPE, useModals } from '../hooks/useModal';
import {
    CartIcon,
    HelpIcon,
    UserIcon,
    ExclamationIcon,
    ExpandMoreIcon,
    ExpandLessIcon,
    UsersIcon,
    BuildingIcon,
    ShipIcon,
    HotelIcon,
    TagsIcon,
    CircleXmarkIcon,
    LinkIcon,
    FileLinesIcon,
    GlobeIcon,
    ShareNodesIcon,
    DollarIcon,
    LocationIcon,
    ArchiveIcon,
    FileInvoiceDollarIcon,
    CardIcon,
    EnvelopeIcon,
    HistoryIcon
} from '../icons';
import { CAPABILITIES } from '../capabilities/capabilities';
import { useCapability } from '../hooks/useCapabilities';
import { releaseGuestLock, stopLockRefresh } from '../tools/softlock';
import { useGuestFields } from '../hooks/useGuestFields';
import { createGuest } from '../actions/user';
import { useNotify } from '../hooks/useNotify';
import { useDispatch } from 'react-redux';
import useKeyboardShortcuts from '../hooks/useKeyboardShortcuts';

const ACTIVITER_USER_MANUAL =
    'https://scribehow.com/page/Travel_Agent_Onboarding__jmL7gQgsRoS_NnCP8yIvzA';

const ADMIN_MENU_SECTIONS = [
    {
        items: [
            {
                label: 'Clients',
                to: '/admin/client/list?type=client',
                capability: CAPABILITIES.MANAGE_CLIENTS,
                Icon: UsersIcon
            },
            {
                label: 'Vendors',
                to: '/admin/client/list?type=vendor',
                capability: CAPABILITIES.MANAGE_VENDORS,
                Icon: BuildingIcon
            },
            {
                label: 'Activities',
                to: '/admin/activities/list',
                capability: CAPABILITIES.MANAGE_ACTIVITIES,
                Icon: ShipIcon
            }
        ]
    },
    {
        title: 'Global settings',
        items: [
            {
                label: 'Activity tags',
                to: '/admin/tags/list',
                capability: CAPABILITIES.MANAGE_ACTIVITY_TAGS,
                Icon: TagsIcon
            },
            {
                label: 'Cancel Reasons',
                to: '/admin/cancelreasons',
                capability: CAPABILITIES.MANAGE_CANCEL_REASONS,
                Icon: CircleXmarkIcon
            },
            {
                label: 'Chains',
                to: '/admin/chains/list',
                capability: CAPABILITIES.MANAGE_CHAINS,
                Icon: LinkIcon
            },
            {
                label: 'Hotels',
                to: '/admin/hotels',
                capability: CAPABILITIES.MANAGE_HOTELS,
                Icon: HotelIcon
            },
            {
                label: 'Price item names',
                to: '/admin/priceitemnames',
                capability: CAPABILITIES.MANAGE_PRICE_ITEM_NAMES,
                Icon: FileLinesIcon
            },
            {
                label: 'Regions',
                to: '/admin/regions',
                capability: CAPABILITIES.MANAGE_REGIONS,
                Icon: GlobeIcon
            },
            {
                label: 'Sources',
                to: '/admin/sources',
                capability: CAPABILITIES.MANAGE_SOURCES,
                Icon: ShareNodesIcon
            },
            {
                label: 'Source groups',
                to: '/admin/sourcegroups',
                capability: CAPABILITIES.MANAGE_SOURCE_GROUPS,
                Icon: ShareNodesIcon
            },
            {
                label: 'System fees',
                to: '/admin/systemfees',
                capability: CAPABILITIES.MANAGE_SYSTEM_FEES,
                Icon: DollarIcon
            },
            {
                label: 'Terminal locations',
                to: '/admin/terminallocations',
                capability: CAPABILITIES.MANAGE_TERMINAL_LOCATIONS,
                Icon: LocationIcon
            }
        ]
    },
    {
        title: 'Reports',
        items: [
            {
                label: 'Batches',
                to: '/admin/batches/list',
                capability: CAPABILITIES.MANAGE_BATCHES,
                Icon: ArchiveIcon
            },
            {
                label: 'Sales Reports',
                to: '/admin/salesreports',
                capability: CAPABILITIES.MANAGE_SALES_REPORTS,
                Icon: FileInvoiceDollarIcon
            },
            {
                label: 'Payout reports',
                to: '/admin/reports/payout',
                capability: CAPABILITIES.MANAGE_PAYOUT_REPORTS,
                Icon: FileInvoiceDollarIcon
            },
            {
                label: 'Booking payouts',
                to: '/admin/bookingpayouts',
                capability: CAPABILITIES.MANAGE_BOOKING_PAYOUTS,
                Icon: CardIcon
            },
            {
                label: 'Custom reports',
                to: '/admin/reports/custom',
                // capability: CAPABILITIES.MANAGE_CUSTOM_REPORTS
                Icon: FileLinesIcon
            }
        ]
    },
    {
        items: [
            {
                label: 'Emails',
                to: '/emaillog',
                capability: CAPABILITIES.MANAGE_EMAILS,
                Icon: EnvelopeIcon
            },
            {
                label: 'Audit log',
                to: '/admin/auditlog',
                capability: CAPABILITIES.MANAGE_AUDIT_LOGS,
                Icon: HistoryIcon
            }
        ]
    }
];

const ProfileMenu = ({ user, logout }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const history = useHistory();

    const handleLogout = async () => {
        setAnchorEl(null);
        await releaseGuestLock();
        stopLockRefresh();
        logout();
        history.replace('/');
    };

    return (
        <>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem
                    component={Link}
                    to="/guests/me"
                    onClick={() => {
                        setAnchorEl(null);
                    }}
                >
                    Profile
                </MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
            <Button
                color="inherit"
                onClick={e => {
                    setAnchorEl(e.currentTarget);
                }}
            >
                {window.matchMedia('(max-width: 600px)').matches ? (
                    <UserIcon style={{ fontSize: '1.25em' }} />
                ) : (
                    <>
                        {user.role === 'agent' && (
                            <>
                                Agent
                                <br />
                            </>
                        )}
                        {user.firstname?.trim() || user.lastname?.trim() ? (
                            <>
                                {user.firstname} {user.lastname}
                            </>
                        ) : (
                            user.email
                        )}
                    </>
                )}
            </Button>
            {user.role === 'agent' && (
                <IconButton href={ACTIVITER_USER_MANUAL} target="_blank">
                    <HelpIcon />
                </IconButton>
            )}
        </>
    );
};

const AdminMenu = () => {
    const { hasCapability } = useCapability();

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [openSections, setOpenSections] = React.useState({});
    const handleClose = () => setAnchorEl(null);
    const toggleSection = title => setOpenSections(prev => ({ ...prev, [title]: !prev[title] }));

    const visibleSections = ADMIN_MENU_SECTIONS.map(section => ({
        ...section,
        items: section.items.filter(item => hasCapability(item.capability))
    })).filter(section => section.items.length > 0);

    const renderItem = item => {
        const Icon = item.Icon;
        return (
            <MenuItem key={item.label} component={Link} to={item.to} onClick={handleClose}>
                {Icon && (
                    <span
                        style={{
                            width: 20,
                            marginRight: 12,
                            display: 'inline-flex',
                            justifyContent: 'center',
                            color: '#555'
                        }}
                    >
                        <Icon />
                    </span>
                )}
                {item.label}
            </MenuItem>
        );
    };

    return (
        <>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                {visibleSections.flatMap((section, idx) => {
                    const nodes = [];
                    if (idx > 0) {
                        nodes.push(<Divider key={`divider-${idx}`} />);
                    }
                    if (section.title) {
                        const isOpen = !!openSections[section.title];
                        nodes.push(
                            <ListSubheader
                                key={`header-${section.title}`}
                                disableSticky
                                onClick={() => toggleSection(section.title)}
                                style={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    minWidth: 220,
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    letterSpacing: '0.05em',
                                    lineHeight: 1,
                                    padding: '8px 16px',
                                    color: '#888',
                                    outline: 'none'
                                }}
                            >
                                <span>{section.title}</span>
                                {isOpen ? (
                                    <ExpandLessIcon style={{ fontSize: '0.75rem' }} />
                                ) : (
                                    <ExpandMoreIcon style={{ fontSize: '0.75rem' }} />
                                )}
                            </ListSubheader>
                        );
                        if (isOpen) {
                            section.items.forEach(item => nodes.push(renderItem(item)));
                        }
                    } else {
                        section.items.forEach(item => nodes.push(renderItem(item)));
                    }
                    return nodes;
                })}
            </Menu>
            <Button color="inherit" onClick={e => setAnchorEl(e.currentTarget)}>
                Admin
            </Button>
        </>
    );
};

const Header = ({ user, cartItemCount, customer, logout }) => {
    const [guestTableOpen, setGuestTableOpen] = useState(false);
    const { showModal } = useModals();
    const upperMenu = [];
    const location = useLocation();
    const [guest_fields] = useGuestFields();
    const { notifyError } = useNotify();
    const dispatch = useDispatch();
    const history = useHistory();
    let isVendorActivitiesLocation = false;

    const functionKeysForAdmins = [
        'F1 - Profile/Sign Up',
        'F2 - Activities',
        'F3 - Booking list',
        'F4 - Guest list',
        'F5 - Emails',
        'F7 - Clients',
        'F8 - Vendors',
        'F9 - Activities',
        'F10 - Booking payouts'
    ];
    const functionKeysForAgents = [
        'F1 - Profile/Sign Up',
        'F2 - Activities',
        'F3 - Booking list',
        'F4 - Guest list',
        'F5 - Emails',
        'F7 - Onboarding info',
        'F8 - Register guest'
    ];
    const functionKeysForGuests = ['F1 - Profile/Sign Up', 'F2 - Activities'];

    useKeyboardShortcuts({
        user,
        history,
        dispatch,
        showModal,
        notifyError,
        guest_fields,
        setGuestTableOpen,
        createGuest,
        MODAL_TYPE,
        ACTIVITER_USER_MANUAL
    });

    // TODO: clean up this ugly hack
    useEffect(() => {
        window.setGuestTableOpen = newState => {
            setGuestTableOpen(newState);
            window.scrollTo(0, 0);
        };
    }, [setGuestTableOpen]);

    if (!user) {
        upperMenu.push(
            <div id="signup-items" key="signup-items">
                <Button color="inherit" onClick={() => showModal(MODAL_TYPE.SIGNUP)}>
                    Sign-up
                </Button>
            </div>
        );
        upperMenu.push(
            <div id="signin-items" key="signin-items">
                <Button color="inherit" onClick={() => showModal(MODAL_TYPE.SIGNIN)}>
                    Sign-in
                </Button>
            </div>
        );
    }

    if (user && user.role === 'agent') {
        upperMenu.push(
            <div id="profile-items" key="guests">
                <Button
                    color="inherit"
                    onClick={ev => {
                        ev.preventDefault();
                        window.setGuestTableOpen(!guestTableOpen);
                    }}
                >
                    Guest List
                </Button>
            </div>
        );
        upperMenu.push(
            <div id="bookings-list-agent" key="bookings-list-agent">
                <Link to="/client/bookings" className="fg-white">
                    <Button color="inherit">Bookings List</Button>
                </Link>
            </div>
        );
        upperMenu.push(
            <div to="email-log-agent" key="emaillogs-agent">
                <Link to="/emaillog">
                    <Button color="inherit">Emails</Button>
                </Link>
            </div>
        );
    }

    if (user && user.role === 'admin') {
        upperMenu.push(
            <div key="adminmenu">
                <AdminMenu />
            </div>
        );
        upperMenu.push(
            <div id="bookings-list-admin" key="bookings-list-admin">
                <Link to="/client/bookings" className="fg-white">
                    <Button color="inherit">Bookings List</Button>
                </Link>
            </div>
        );
    }

    if (user) {
        upperMenu.push(
            <div
                id="logout-items"
                key="profile"
                style={{ textAlign: 'center', display: 'flex', alignItems: 'center' }}
            >
                <ProfileMenu user={user} logout={logout} />
                {customer && user && user.role !== 'agent' && (
                    <span className="cart-icon-wrapper">
                        <Link to="/cart">
                            <Badge
                                badgeContent={cartItemCount}
                                color="secondary"
                                invisible={cartItemCount === 0}
                            >
                                <CartIcon />
                            </Badge>
                        </Link>
                    </span>
                )}
            </div>
        );
    }

    if (location.pathname.includes('/activities/vendor/')) {
        isVendorActivitiesLocation = true;
    }

    return (
        <>
            <AppBar className="appbar" position="sticky">
                <Toolbar className="appbar-content">
                    <Link to="/" className="appbar-brand">
                        <img src={window.clientData?.logoImage} alt={window.clientData?.name} />
                    </Link>
                    {!isVendorActivitiesLocation && (
                        <>
                            <Tooltip
                                title={
                                    user && user.role === 'admin'
                                        ? functionKeysForAdmins.map(key => (
                                              <div key={key}>{key}</div>
                                          ))
                                        : user && user.role === 'agent'
                                        ? functionKeysForAgents.map(key => (
                                              <div key={key}>{key}</div>
                                          ))
                                        : functionKeysForGuests.map(key => (
                                              <div key={key}>{key}</div>
                                          ))
                                }
                                placement="bottom"
                                arrow
                            >
                                <ExclamationIcon />
                            </Tooltip>
                            <Link to="/" className="fg-white">
                                <Button color="inherit">Activities</Button>
                            </Link>
                            {false && (
                                <Link to="/golf" className="fg-white">
                                    <Button color="inherit">Golf</Button>
                                </Link>
                            )}
                        </>
                    )}

                    {upperMenu}
                </Toolbar>
                <GuestHeaderContainer
                    guestTableOpen={guestTableOpen}
                    setGuestTableOpen={setGuestTableOpen}
                />
            </AppBar>
        </>
    );
};

export default Header;
